import type Map from 'ol/Map';
import type BaseLayer from 'ol/layer/Base';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import type { FeatureLike } from 'ol/Feature';
import Draw from 'ol/interaction/Draw';
import Modify from 'ol/interaction/Modify';
import Select from 'ol/interaction/Select';
import Snap from 'ol/interaction/Snap';
import Translate from 'ol/interaction/Translate';
import type Interaction from 'ol/interaction/Interaction';
import GeoJSON from 'ol/format/GeoJSON';
import { Geometry, LineString, MultiLineString, MultiPoint, MultiPolygon, Point, Polygon } from 'ol/geom';
import { Circle as CircleStyle, Fill, RegularShape, Stroke, Style } from 'ol/style';
import { toLonLat } from 'ol/proj';
import { altKeyOnly, singleClick } from 'ol/events/condition';
import { unByKey } from 'ol/Observable';
import type { EventsKey } from 'ol/events';
import type { MapBrowserEvent } from 'ol';
import type { Coordinate } from 'ol/coordinate';
import { EditApi, EditError, Geometry as GeoJsonGeometry, OverlapPart, editError } from './editingApi';

/**
 * One editing session of one company layer, on an OpenLayers map: the tools to add, reshape, move and delete
 * features, to continue a line and to join two, with snapping, undo and redo, until the changes are saved or
 * discarded. Nothing is written while drawing - only {@link save} sends the changes, one request per feature - so
 * the person can try, undo and change their mind, as in QGIS's edit mode.
 *
 * <p>How it follows the desktop tools (QGIS digitizing, ArcGIS Pro editing), as far as a browser makes sense:
 * <ul>
 *   <li>Snapping is always on (the owner's decision): to the vertices and edges of the edited layer and of every other
 *       company layer shown, within 10 pixels, with a marker where the pointer will land (a circle on a vertex, a
 *       diamond on an edge). Tracing along a neighbour's edge (QGIS "tracing", ArcGIS "trace") can be switched on.</li>
 *   <li>Reshaping works on the vertices of any feature under the pointer; a vertex shared by two features moves for
 *       both (topological editing). Alt+click or Backspace removes a vertex, dragging an edge adds one.</li>
 *   <li>A polygon that lies over its neighbours is shown with the shared part in red, and the person chooses: clip it
 *       to the neighbours (QGIS "avoid intersections"), keep it as drawn, or cancel.</li>
 *   <li>"Continue line" extends a line from whichever end is clicked (ArcGIS "continue feature"); "Join lines" makes two
 *       lines that meet end to end one, the first keeping its values (a merge).</li>
 * </ul>
 *
 * <p>The React toolbar listens with {@link subscribe}; the questions it asks the person (attributes, an overlap) are
 * the {@link SessionHooks} it is made with.
 */

export type EditTool = 'add' | 'modify' | 'move' | 'delete' | 'continue' | 'join';
export type DrawKind = 'Point' | 'LineString' | 'Polygon';
export type LayerShape = 'point' | 'line' | 'area' | 'any';
export type OverlapAnswer = 'clip' | 'allow' | 'cancel';

export interface OverlapQuestion {
  overlaps: OverlapPart[];
  overlapArea: number;
  clipped: GeoJsonGeometry | null;
  clipEmpty: boolean;
  isNew: boolean;
}

export interface SessionHooks {
  /** The polygon lies over its neighbours (shown in red): clip, keep or cancel. */
  askOverlap(question: OverlapQuestion): Promise<OverlapAnswer>;
  /** The values of a feature, by the names its properties carry; null when the person cancels. */
  askAttributes(feature: Feature, isNew: boolean): Promise<Record<string, unknown> | null>;
  /** A message for the person: an i18n key with its values. */
  notify(kind: 'info' | 'error' | 'success', key: string, params?: Record<string, unknown>): void;
  /** A refusal of the server, to be worded. */
  failed?(error: EditError): void;
}

export interface SessionOptions {
  shape: LayerShape;
  api: EditApi;
  hooks: SessionHooks;
  /** The company layer, hidden while editing when it is drawn as a heatmap or clusters (the edit layer shows it). */
  hideLayer?: BaseLayer;
}

export interface SessionState {
  tool: EditTool;
  drawKind: DrawKind;
  canUndo: boolean;
  canRedo: boolean;
  pending: number;
  busy: boolean;
  drawing: boolean;
  selected: Feature | null;
  snapped: 'vertex' | 'edge' | null;
  trace: boolean;
  picks: number;
}

interface Command {
  undo(): void;
  redo(): void;
}

interface Snapshot {
  geometry: string | null;
  props: Record<string, unknown>;
}

const SNAP_PIXELS = 10;
const HIT_PIXELS = 6;
const END_PIXELS = 12;

const ORANGE = '#f97316';
const GREEN = '#16a34a';
const BLUE = '#2563eb';
const RED = '#dc2626';
const SNAP = '#d946ef';

export function drawKindOf(shape: LayerShape, chosen: DrawKind): DrawKind {
  if (shape === 'point') return 'Point';
  if (shape === 'line') return 'LineString';
  if (shape === 'area') return 'Polygon';
  return chosen;
}

function isPolygonal(g: Geometry | undefined): g is Polygon | MultiPolygon {
  return g instanceof Polygon || g instanceof MultiPolygon;
}

function isLinear(g: Geometry | undefined): g is LineString | MultiLineString {
  return g instanceof LineString || g instanceof MultiLineString;
}

/** The values a feature carries, without its geometry and what the map keeps for itself (@...). */
export function valuesOf(f: Feature): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const geomName = f.getGeometryName();
  for (const [k, v] of Object.entries(f.getProperties())) {
    if (k === geomName || k.startsWith('@')) continue;
    out[k] = v;
  }
  return out;
}

function same(a: unknown, b: unknown): boolean {
  const empty = (v: unknown) => v === undefined || v === null || v === '';
  if (empty(a) && empty(b)) return true;
  return JSON.stringify(a) === JSON.stringify(b);
}

export class EditSession {
  readonly map: Map;
  readonly source: VectorSource;
  readonly editLayer: VectorLayer<VectorSource>;
  readonly overlay: VectorLayer<VectorSource>;
  readonly options: SessionOptions;
  private readonly format = new GeoJSON();
  private interactions: Interaction[] = [];
  private keys: EventsKey[] = [];
  private toolKeys: EventsKey[] = [];
  private undoStack: Command[] = [];
  private redoStack: Command[] = [];
  private readonly snapshots = new globalThis.Map<Feature, Snapshot>();
  private readonly added = new Set<Feature>();
  private readonly deleted = new Set<Feature>();
  private readonly listeners = new Set<() => void>();
  private draw: Draw | null = null;
  private modify: Modify | null = null;
  private select: Select | null = null;
  private picks: Feature[] = [];
  private hiddenWasVisible = false;
  private readonly snapMarker = new Feature<Point>();
  private readonly onKey = (e: KeyboardEvent) => this.key(e);
  state: SessionState;

  constructor(map: Map, source: VectorSource, options: SessionOptions) {
    this.map = map;
    this.source = source;
    this.options = options;
    this.state = {
      tool: 'add',
      drawKind: drawKindOf(options.shape, 'Polygon'),
      canUndo: false,
      canRedo: false,
      pending: 0,
      busy: false,
      drawing: false,
      selected: null,
      snapped: null,
      trace: false,
      picks: 0,
    };
    this.editLayer = new VectorLayer({ source, style: (f, r) => this.editStyle(f, r), zIndex: 85 });
    this.editLayer.set('name', 'edit-session');
    this.overlay = new VectorLayer({ source: new VectorSource(), style: (f) => overlayStyle(f), zIndex: 1000 });
    this.overlay.set('name', 'edit-overlay');
    this.snapMarker.set('role', 'snap-vertex');
    map.addLayer(this.editLayer);
    map.addLayer(this.overlay);
    if (options.hideLayer) {
      this.hiddenWasVisible = options.hideLayer.getVisible();
      options.hideLayer.setVisible(false);
    }
    document.addEventListener('keydown', this.onKey);
    this.setTool('add');
  }

  // ------------------------------------------------------------------------------------------ state

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getState(): SessionState {
    return this.state;
  }

  private emit(patch: Partial<SessionState> = {}): void {
    this.state = {
      ...this.state,
      ...patch,
      canUndo: this.undoStack.length > 0,
      canRedo: this.redoStack.length > 0,
      pending: this.pendingCount(),
      picks: this.picks.length,
    };
    this.listeners.forEach((l) => l());
  }

  private get projection() {
    return this.map.getView().getProjection();
  }

  toGeoJson(g: Geometry): GeoJsonGeometry {
    return this.format.writeGeometryObject(g, { dataProjection: 'EPSG:4326', featureProjection: this.projection, decimals: 9 }) as GeoJsonGeometry;
  }

  fromGeoJson(g: GeoJsonGeometry): Geometry {
    return this.format.readGeometry(g, { dataProjection: 'EPSG:4326', featureProjection: this.projection });
  }

  /** So many pixels on the screen, in metres on the ground, at the view's centre. */
  metres(pixels: number): number {
    const view = this.map.getView();
    const res = view.getResolution() ?? 1;
    const lat = toLonLat(view.getCenter() ?? [0, 0], this.projection)[1];
    return Math.max(0.1, Math.min(500, res * pixels * Math.cos((lat * Math.PI) / 180)));
  }

  // ------------------------------------------------------------------------------------------ tools

  setDrawKind(kind: DrawKind): void {
    this.state = { ...this.state, drawKind: drawKindOf(this.options.shape, kind) };
    if (this.state.tool === 'add') this.setTool('add');
    else this.emit();
  }

  setTrace(on: boolean): void {
    this.state = { ...this.state, trace: on };
    this.draw?.setTrace(on);
    this.emit();
  }

  setTool(tool: EditTool): void {
    this.clearTool();
    this.state = { ...this.state, tool, selected: null, drawing: false, snapped: null };
    const target = this.map.getTargetElement();
    if (target) target.style.cursor = tool === 'add' || tool === 'continue' ? 'crosshair' : tool === 'delete' ? 'not-allowed' : '';
    switch (tool) {
      case 'add': {
        this.useDraw(new Draw({
          type: drawKindOf(this.options.shape, this.state.drawKind),
          stopClick: true,
          trace: this.state.trace,
          traceSource: this.source,
          style: sketchStyle,
        }), (f) => this.drawn(f));
        break;
      }
      case 'modify': {
        this.useSelect();
        const modify = new Modify({
          source: this.source,
          pixelTolerance: SNAP_PIXELS,
          deleteCondition: (e) => altKeyOnly(e) && singleClick(e),
          style: vertexHandleStyle,
        });
        this.modify = modify;
        let before = new globalThis.Map<Feature, Geometry>();
        modify.on('modifystart', (e) => {
          before = new globalThis.Map();
          e.features.forEach((f) => {
            const g = (f as Feature).getGeometry();
            if (g) before.set(f as Feature, g.clone());
          });
        });
        modify.on('modifyend', () => { void this.reshaped(before); });
        this.add(modify);
        break;
      }
      case 'move': {
        const translate = new Translate({ layers: [this.editLayer], hitTolerance: HIT_PIXELS });
        let before = new globalThis.Map<Feature, Geometry>();
        translate.on('translatestart', (e) => {
          before = new globalThis.Map();
          e.features.forEach((f) => {
            const g = (f as Feature).getGeometry();
            if (g) before.set(f as Feature, g.clone());
          });
        });
        translate.on('translateend', () => { void this.reshaped(before); });
        this.add(translate);
        break;
      }
      case 'delete':
        this.toolKeys.push(this.map.on('click', (e) => this.deleteAt(e)));
        break;
      case 'continue':
        this.toolKeys.push(this.map.on('click', (e) => this.continueAt(e)));
        this.toolKeys.push(this.map.on('pointermove', (e) => this.showEnd(e)));
        break;
      case 'join':
        this.toolKeys.push(this.map.on('click', (e) => { void this.pickToJoin(e); }));
        break;
    }
    this.addSnaps();
    this.emit();
  }

  private add(i: Interaction): void {
    this.map.addInteraction(i);
    this.interactions.push(i);
  }

  private useDraw(draw: Draw, done: (f: Feature) => void): void {
    this.draw = draw;
    draw.on('drawstart', () => this.emit({ drawing: true }));
    draw.on('drawabort', () => this.emit({ drawing: false }));
    draw.on('drawend', (e) => {
      this.emit({ drawing: false });
      done(e.feature as Feature);
    });
    this.add(draw);
  }

  private useSelect(): void {
    const select = new Select({ layers: [this.editLayer], hitTolerance: HIT_PIXELS, style: selectedStyle });
    select.on('select', () => this.emit({ selected: (select.getFeatures().item(0) as Feature) ?? null }));
    this.select = select;
    this.add(select);
  }

  /**
   * Snapping to the vertices and edges of the edited layer and of the other company layers shown. Added after the
   * tool's own interactions, as OpenLayers asks, so that the pointer is moved before they see it.
   */
  private addSnaps(): void {
    const sources: VectorSource[] = [this.source];
    this.map.getLayers().forEach((l) => {
      const own = l.get('featureSource');
      if (l.get('companyLayer') && l.getVisible() && own instanceof VectorSource && own !== this.source) sources.push(own);
    });
    const marker = this.overlay.getSource()!;
    for (const s of sources) {
      const snap = new Snap({ source: s, pixelTolerance: SNAP_PIXELS, vertex: true, edge: true });
      snap.on('snap', (e) => {
        const kind = e.segment ? 'edge' : 'vertex';
        this.snapMarker.setGeometry(new Point(e.vertex));
        this.snapMarker.set('role', kind === 'edge' ? 'snap-edge' : 'snap-vertex');
        if (!marker.hasFeature(this.snapMarker)) marker.addFeature(this.snapMarker);
        if (this.state.snapped !== kind) this.emit({ snapped: kind });
      });
      snap.on('unsnap', () => {
        if (marker.hasFeature(this.snapMarker)) marker.removeFeature(this.snapMarker);
        if (this.state.snapped) this.emit({ snapped: null });
      });
      this.add(snap);
    }
  }

  private clearTool(): void {
    this.interactions.forEach((i) => this.map.removeInteraction(i));
    this.interactions = [];
    this.toolKeys.forEach((k) => unByKey(k));
    this.toolKeys = [];
    this.draw = null;
    this.modify = null;
    this.select = null;
    this.picks = [];
    this.overlay.getSource()!.clear();
  }

  // ------------------------------------------------------------------------------------------ add

  private async drawn(feature: Feature): Promise<void> {
    if (isPolygonal(feature.getGeometry())) {
      const answer = await this.checkPolygon(feature, undefined, true);
      if (answer === 'cancel') return;
    }
    const values = await this.options.hooks.askAttributes(feature, true);
    if (values === null) return;
    for (const [k, v] of Object.entries(values)) {
      if (v !== null && v !== undefined && v !== '') feature.set(k, v);
    }
    feature.set('@new', true);
    const add = () => {
      this.source.addFeature(feature);
      this.added.add(feature);
    };
    const remove = () => {
      this.source.removeFeature(feature);
      this.added.delete(feature);
    };
    add();
    this.push({ undo: remove, redo: add });
  }

  // ------------------------------------------------------------------------------------------ reshape and move

  private async reshaped(before: globalThis.Map<Feature, Geometry>): Promise<void> {
    const changed: [Feature, Geometry, Geometry][] = [];
    before.forEach((g, f) => {
      const now = f.getGeometry();
      if (now && JSON.stringify(this.toGeoJson(now)) !== JSON.stringify(this.toGeoJson(g))) changed.push([f, g, now.clone()]);
    });
    if (changed.length === 0) return;
    changed.forEach(([f, g]) => this.touch(f, g));
    // A polygon now lying over a neighbour: the person decides, as when it was drawn.
    for (const [f] of changed) {
      if (!isPolygonal(f.getGeometry())) continue;
      const id = f.getId() === undefined ? undefined : String(f.getId());
      const answer = await this.checkPolygon(f, id, false);
      if (answer === 'cancel') {
        changed.forEach(([x, y]) => x.setGeometry(y.clone()));
        this.emit();
        return;
      }
    }
    const after = changed.map(([f, , a]) => [f, f.getGeometry()?.clone() ?? a] as [Feature, Geometry]);
    this.push({
      undo: () => changed.forEach(([f, g]) => f.setGeometry(g.clone())),
      redo: () => after.forEach(([f, a]) => f.setGeometry(a.clone())),
    });
  }

  /**
   * The check of a drawn or changed polygon: first in the browser - does it come near any polygon at all? - and only
   * then on the server, which knows the exact overlaps and repairs a polygon that crosses itself. The overlap is
   * shown in red and the person answers; the feature is changed accordingly (clipped, or marked to be kept).
   */
  async checkPolygon(feature: Feature, id: string | undefined, isNew: boolean): Promise<'ok' | 'cancel'> {
    const g = feature.getGeometry();
    if (!g) return 'cancel';
    if (!this.mayOverlap(feature)) return 'ok';
    this.emit({ busy: true });
    try {
      const result = await this.options.api.check(this.toGeoJson(g), id, this.metres(SNAP_PIXELS));
      if (result.fixed) {
        feature.setGeometry(this.fromGeoJson(result.geometry));
        this.options.hooks.notify('info', 'editing.fixed');
      }
      if (result.overlaps.length === 0) return 'ok';
      return await this.ask(feature, {
        overlaps: result.overlaps,
        overlapArea: result.overlapArea ?? 0,
        clipped: result.clipped,
        clipEmpty: result.clipEmpty,
        isNew,
      });
    } catch (e) {
      this.fail(e);
      return 'cancel';
    } finally {
      this.emit({ busy: false });
    }
  }

  /** The quick check: does the polygon's box meet the box of any other polygon the snapping sees? */
  private mayOverlap(feature: Feature): boolean {
    const extent = feature.getGeometry()!.getExtent();
    const sources: VectorSource[] = [this.source];
    this.map.getLayers().forEach((l) => {
      const own = l.get('featureSource');
      if (l.get('companyLayer') && own instanceof VectorSource && own !== this.source) sources.push(own);
    });
    return sources.some((s) => s.getFeaturesInExtent(extent).some((o) => o !== feature && isPolygonal(o.getGeometry())));
  }

  private async ask(feature: Feature, q: OverlapQuestion): Promise<'ok' | 'cancel'> {
    const overlay = this.overlay.getSource()!;
    const shown: Feature[] = q.overlaps.map((o) => new Feature({ geometry: this.fromGeoJson(o.geometry), role: 'overlap' }));
    if (q.clipped) shown.push(new Feature({ geometry: this.fromGeoJson(q.clipped), role: 'clipped' }));
    overlay.addFeatures(shown);
    let answer: OverlapAnswer;
    try {
      answer = await this.options.hooks.askOverlap(q);
    } finally {
      shown.forEach((f) => overlay.hasFeature(f) && overlay.removeFeature(f));
    }
    if (answer === 'clip') {
      if (!q.clipped || q.clipEmpty) {
        this.options.hooks.notify('error', 'editing.errors.clip_empty');
        return 'cancel';
      }
      feature.setGeometry(this.fromGeoJson(q.clipped));
      feature.set('@overlap', 'clip');
      return 'ok';
    }
    if (answer === 'allow') {
      feature.set('@overlap', 'allow');
      return 'ok';
    }
    return 'cancel';
  }

  // ------------------------------------------------------------------------------------------ delete

  featureAt(pixel: number[], filter: (f: Feature) => boolean = () => true): Feature | null {
    let found: Feature | null = null;
    this.map.forEachFeatureAtPixel(pixel, (f) => {
      if (f instanceof Feature && filter(f)) {
        found = f;
        return true;
      }
      return false;
    }, { layerFilter: (l) => l === this.editLayer, hitTolerance: HIT_PIXELS });
    return found;
  }

  private deleteAt(e: MapBrowserEvent<any>): void {
    const f = this.featureAt(e.pixel);
    if (f) this.remove(f);
  }

  /** The selected feature, or the one given, out of the layer (until undone or discarded). */
  remove(f: Feature | null = this.state.selected): void {
    if (!f || !this.source.hasFeature(f)) return;
    const isNew = this.added.has(f);
    const out = () => {
      this.source.removeFeature(f);
      if (isNew) this.added.delete(f);
      else this.deleted.add(f);
    };
    const back = () => {
      this.source.addFeature(f);
      if (isNew) this.added.add(f);
      else this.deleted.delete(f);
    };
    this.select?.getFeatures().clear();
    out();
    this.push({ undo: back, redo: out });
    this.emit({ selected: null });
  }

  // ------------------------------------------------------------------------------------------ continue a line

  /** The end of a line nearest the pointer, within a few pixels: which feature, which part, which end. */
  private endNear(pixel: number[]): { f: Feature; part: number; atStart: boolean; at: Coordinate } | null {
    let best: { f: Feature; part: number; atStart: boolean; at: Coordinate; d: number } | null = null;
    const here = this.map.getCoordinateFromPixel(pixel);
    const res = this.map.getView().getResolution() ?? 1;
    const box = [here[0] - END_PIXELS * res, here[1] - END_PIXELS * res, here[0] + END_PIXELS * res, here[1] + END_PIXELS * res];
    for (const f of this.source.getFeaturesInExtent(box)) {
      const g = f.getGeometry();
      if (!isLinear(g)) continue;
      const parts = g instanceof MultiLineString ? g.getLineStrings() : [g];
      parts.forEach((p, i) => {
        const c = p.getCoordinates();
        for (const [at, atStart] of [[c[0], true], [c[c.length - 1], false]] as [Coordinate, boolean][]) {
          const px = this.map.getPixelFromCoordinate(at);
          const d = Math.hypot(px[0] - pixel[0], px[1] - pixel[1]);
          if (d <= END_PIXELS && (!best || d < best.d)) best = { f, part: i, atStart, at, d };
        }
      });
    }
    return best;
  }

  private showEnd(e: MapBrowserEvent<any>): void {
    if (this.state.drawing) return;
    const overlay = this.overlay.getSource()!;
    overlay.getFeatures().filter((f) => f.get('role') === 'end').forEach((f) => overlay.removeFeature(f));
    const end = this.endNear(e.pixel);
    if (end) overlay.addFeature(new Feature({ geometry: new Point(end.at), role: 'end' }));
  }

  private continueAt(e: MapBrowserEvent<any>): void {
    if (this.state.drawing) return;
    const end = this.endNear(e.pixel);
    if (!end) {
      this.options.hooks.notify('info', 'editing.hint_continue');
      return;
    }
    this.continueLine(end.f, end.part, end.atStart);
  }

  /** Draws on from one end of a line (its start: the line is drawn on backwards, and turned round again after). */
  continueLine(f: Feature, part: number, atStart: boolean): void {
    const g = f.getGeometry() as LineString | MultiLineString;
    const parts = g instanceof MultiLineString ? g.getLineStrings() : [g];
    const coords = parts[part].getCoordinates();
    if (atStart) coords.reverse();
    const sketch = new Feature(new LineString(coords));
    const before = g.clone();
    // Only the drawing: no other click handler while the line is drawn on.
    this.interactions.forEach((i) => this.map.removeInteraction(i));
    this.interactions = [];
    const draw = new Draw({ type: 'LineString', stopClick: true, trace: this.state.trace, traceSource: this.source, style: sketchStyle });
    const back = () => setTimeout(() => this.setTool('continue'), 0);
    draw.on('drawabort', back);
    this.useDraw(draw, (drawn) => {
      const line = (drawn.getGeometry() as LineString).getCoordinates();
      if (atStart) line.reverse();
      let after: Geometry;
      if (g instanceof MultiLineString) {
        const all = parts.map((p) => p.getCoordinates());
        all[part] = line;
        after = new MultiLineString(all);
      } else {
        after = new LineString(line);
      }
      this.touch(f, before);
      f.setGeometry(after);
      const kept = after.clone();
      this.push({ undo: () => f.setGeometry(before.clone()), redo: () => f.setGeometry(kept.clone()) });
      back();
    });
    this.addSnaps();
    this.overlay.getSource()!.clear();
    draw.extend(sketch);
    this.emit({ drawing: true });
  }

  // ------------------------------------------------------------------------------------------ join two lines

  private async pickToJoin(e: MapBrowserEvent<any>): Promise<void> {
    if (this.state.busy) return;
    const f = this.featureAt(e.pixel, (x) => isLinear(x.getGeometry()));
    if (!f) return;
    if (this.picks.includes(f)) return;
    this.picks.push(f);
    this.overlay.getSource()!.addFeature(new Feature({ geometry: f.getGeometry()!.clone(), role: 'pick' }));
    this.emit();
    if (this.picks.length === 2) {
      const [a, b] = this.picks;
      await this.join(a, b);
      this.picks = [];
      this.overlay.getSource()!.clear();
      this.emit();
    }
  }

  /** {@code b} joined onto {@code a} where their ends meet: a takes the joined shape and keeps its values; b goes. */
  async join(a: Feature, b: Feature): Promise<boolean> {
    this.emit({ busy: true });
    try {
      const result = await this.options.api.joinPreview(this.toGeoJson(a.getGeometry()!), this.toGeoJson(b.getGeometry()!),
        this.metres(SNAP_PIXELS));
      const before = a.getGeometry()!.clone();
      const after = this.fromGeoJson(result.geometry);
      this.touch(a, before);
      const bNew = this.added.has(b);
      const apply = () => {
        a.setGeometry(after.clone());
        this.source.removeFeature(b);
        if (bNew) this.added.delete(b);
        else this.deleted.add(b);
      };
      const undo = () => {
        a.setGeometry(before.clone());
        this.source.addFeature(b);
        if (bNew) this.added.add(b);
        else this.deleted.delete(b);
      };
      apply();
      this.push({ undo, redo: apply });
      this.options.hooks.notify('success', 'editing.joined', { gap: result.gap.toFixed(2) });
      return true;
    } catch (e) {
      this.fail(e);
      return false;
    } finally {
      this.emit({ busy: false });
    }
  }

  // ------------------------------------------------------------------------------------------ attributes

  /** The selected feature's values, asked of the person again. */
  async editAttributes(f: Feature | null = this.state.selected): Promise<void> {
    if (!f) return;
    const values = await this.options.hooks.askAttributes(f, this.added.has(f));
    if (values === null) return;
    this.touch(f);
    const before = valuesOf(f);
    const apply = (v: Record<string, unknown>) => {
      for (const k of new Set([...Object.keys(before), ...Object.keys(values)])) {
        const x = v[k];
        if (x === undefined || x === null || x === '') f.unset(k);
        else f.set(k, x);
      }
    };
    apply(values);
    this.push({ undo: () => apply(before), redo: () => apply(values) });
  }

  // ------------------------------------------------------------------------------------------ undo

  private push(c: Command): void {
    this.undoStack.push(c);
    this.redoStack = [];
    this.emit();
  }

  undo(): void {
    if (this.state.drawing && this.draw) {
      this.draw.removeLastPoint();
      return;
    }
    const c = this.undoStack.pop();
    if (!c) return;
    c.undo();
    this.redoStack.push(c);
    this.select?.getFeatures().clear();
    this.emit({ selected: null });
  }

  redo(): void {
    const c = this.redoStack.pop();
    if (!c) return;
    c.redo();
    this.undoStack.push(c);
    this.emit();
  }

  /** A saved feature as it was before this session first changed it: what save compares with. */
  private touch(f: Feature, geometryBefore?: Geometry): void {
    if (this.added.has(f) || f.getId() === undefined || this.snapshots.has(f)) return;
    const g = geometryBefore ?? f.getGeometry();
    this.snapshots.set(f, { geometry: g ? JSON.stringify(this.toGeoJson(g)) : null, props: valuesOf(f) });
  }

  private changedOf(f: Feature, s: Snapshot): { geometry?: GeoJsonGeometry; properties?: Record<string, unknown> } | null {
    const out: { geometry?: GeoJsonGeometry; properties?: Record<string, unknown> } = {};
    const g = f.getGeometry();
    if (g) {
      const now = this.toGeoJson(g);
      if (JSON.stringify(now) !== s.geometry) out.geometry = now;
    }
    const values = valuesOf(f);
    const props: Record<string, unknown> = {};
    for (const k of new Set([...Object.keys(values), ...Object.keys(s.props)])) {
      if (!same(values[k], s.props[k])) props[k] = values[k] === undefined || values[k] === '' ? null : values[k];
    }
    if (Object.keys(props).length) out.properties = props;
    return out.geometry || out.properties ? out : null;
  }

  private pendingCount(): number {
    let n = this.added.size + this.deleted.size;
    this.snapshots.forEach((s, f) => {
      if (!this.deleted.has(f) && this.source.hasFeature(f) && this.changedOf(f, s)) n++;
    });
    return n;
  }

  // ------------------------------------------------------------------------------------------ save

  /**
   * Every change sent, one feature at a time: withdrawn ones first (they make room), then changed, then new ones.
   * A polygon the server finds over a neighbour (one drawn in this session, which the server could not see when it
   * was checked) is put to the person again, and saving goes on with their answer. On another refusal saving stops
   * at that feature, which is selected, and the rest stays to be saved.
   */
  async save(): Promise<boolean> {
    if (this.state.busy) return false;
    this.emit({ busy: true });
    const api = this.options.api;
    let current: Feature | null = null;
    let done = 0;
    try {
      for (const f of [...this.deleted]) {
        current = f;
        await api.remove(String(f.getId()));
        this.deleted.delete(f);
        this.snapshots.delete(f);
        done++;
      }
      for (const [f, s] of [...this.snapshots]) {
        current = f;
        if (!this.source.hasFeature(f)) continue;
        const change = this.changedOf(f, s);
        if (change) {
          await api.update(String(f.getId()), { ...change, overlap: change.geometry ? (f.get('@overlap') ?? 'refuse') : undefined });
          done++;
        }
        this.snapshots.delete(f);
      }
      for (const f of [...this.added]) {
        current = f;
        const values = valuesOf(f);
        const saved = await api.create(this.toGeoJson(f.getGeometry()!), values, f.get('@overlap') ?? 'refuse');
        this.added.delete(f);
        f.unset('@new');
        f.setId(saved.id);
        done++;
      }
    } catch (e) {
      const err = editError(e);
      this.emit({ busy: false });
      if (err.code === 'edit.overlap' && current) {
        const answer = await this.ask(current, {
          overlaps: err.body.overlaps ?? [],
          overlapArea: err.body.overlapArea ?? 0,
          clipped: err.body.clipped ?? null,
          clipEmpty: !err.body.clipped,
          isNew: this.added.has(current),
        });
        if (answer === 'ok') return this.save();
      } else {
        this.fail(e);
      }
      if (current && this.source.hasFeature(current)) {
        this.select?.getFeatures().clear();
        this.select?.getFeatures().push(current);
        this.emit({ selected: current });
      }
      return false;
    }
    this.undoStack = [];
    this.redoStack = [];
    this.source.refresh();
    this.emit({ busy: false, selected: null });
    this.options.hooks.notify('success', 'editing.saved', { count: done });
    return true;
  }

  /** Every change of this session let go: the layer read again as it is saved. */
  discard(): void {
    this.draw?.abortDrawing();
    this.undoStack = [];
    this.redoStack = [];
    this.snapshots.clear();
    this.added.clear();
    this.deleted.clear();
    this.select?.getFeatures().clear();
    this.source.refresh();
    this.setTool(this.state.tool);
  }

  get dirty(): boolean {
    return this.pendingCount() > 0;
  }

  dispose(): void {
    this.clearTool();
    this.keys.forEach((k) => unByKey(k));
    document.removeEventListener('keydown', this.onKey);
    this.map.removeLayer(this.editLayer);
    this.map.removeLayer(this.overlay);
    const target = this.map.getTargetElement();
    if (target) target.style.cursor = '';
    if (this.options.hideLayer) this.options.hideLayer.setVisible(this.hiddenWasVisible);
    this.listeners.clear();
  }

  private fail(e: unknown): void {
    const err = editError(e);
    if (this.options.hooks.failed) this.options.hooks.failed(err);
    else this.options.hooks.notify('error', 'editing.errors.failed', { message: err.message });
  }

  // ------------------------------------------------------------------------------------------ keys

  private key(e: KeyboardEvent): void {
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
    const ctrl = e.ctrlKey || e.metaKey;
    if (ctrl && (e.key === 'z' || e.key === 'Z')) {
      e.preventDefault();
      if (e.shiftKey) this.redo();
      else this.undo();
      return;
    }
    if (ctrl && (e.key === 'y' || e.key === 'Y')) {
      e.preventDefault();
      this.redo();
      return;
    }
    if (e.key === 'Escape') {
      if (this.state.drawing && this.draw) this.draw.abortDrawing();
      else if (this.picks.length) {
        this.picks = [];
        this.overlay.getSource()!.clear();
        this.emit();
      } else {
        this.select?.getFeatures().clear();
        this.emit({ selected: null });
      }
      return;
    }
    if (e.key === 'Backspace' || e.key === 'Delete') {
      if (this.state.drawing && this.draw) {
        e.preventDefault();
        this.draw.removeLastPoint();
      } else if (this.modify && this.modify.removePoint()) {
        e.preventDefault();
      } else if (this.state.selected) {
        e.preventDefault();
        this.remove(this.state.selected);
      }
    }
  }

  // ------------------------------------------------------------------------------------------ looks

  private editStyle(f: FeatureLike, _resolution: number): Style[] {
    const fresh = !!f.get('@new');
    const colour = fresh ? GREEN : ORANGE;
    const g = f.getGeometry();
    if (g instanceof Point || g instanceof MultiPoint) {
      return [new Style({ image: new CircleStyle({ radius: 6, fill: new Fill({ color: 'rgba(255,255,255,0.7)' }), stroke: new Stroke({ color: colour, width: 2 }) }) })];
    }
    return [new Style({
      stroke: new Stroke({ color: colour, width: fresh ? 2.5 : 1.5 }),
      // nearly clear, but there: a click inside a polygon picks it
      fill: new Fill({ color: fresh ? 'rgba(22,163,74,0.12)' : 'rgba(249,115,22,0.04)' }),
    })];
  }
}

// ------------------------------------------------------------------------------------------ styles

function verticesOf(f: FeatureLike): MultiPoint | undefined {
  const g = f.getGeometry() as Geometry | undefined;
  if (!g) return undefined;
  if (g instanceof Polygon) return new MultiPoint(g.getCoordinates().flat());
  if (g instanceof MultiPolygon) return new MultiPoint(g.getCoordinates().flat(2));
  if (g instanceof LineString) return new MultiPoint(g.getCoordinates());
  if (g instanceof MultiLineString) return new MultiPoint(g.getCoordinates().flat());
  return undefined;
}

const vertexImage = new RegularShape({ points: 4, radius: 4, angle: Math.PI / 4, fill: new Fill({ color: '#ffffff' }), stroke: new Stroke({ color: BLUE, width: 1.5 }) });

function selectedStyle(): Style[] {
  return [
    new Style({
      stroke: new Stroke({ color: BLUE, width: 3 }),
      fill: new Fill({ color: 'rgba(37,99,235,0.12)' }),
      image: new CircleStyle({ radius: 7, fill: new Fill({ color: 'rgba(37,99,235,0.3)' }), stroke: new Stroke({ color: BLUE, width: 2 }) }),
    }),
    new Style({ image: vertexImage, geometry: (x) => verticesOf(x) }),
  ];
}

const sketchStyle = [
  new Style({
    stroke: new Stroke({ color: '#ffffff', width: 5 }),
    fill: new Fill({ color: 'rgba(37,99,235,0.15)' }),
  }),
  new Style({
    stroke: new Stroke({ color: BLUE, width: 2.5 }),
    image: new CircleStyle({ radius: 5, fill: new Fill({ color: BLUE }), stroke: new Stroke({ color: '#ffffff', width: 1.5 }) }),
  }),
];

const vertexHandleStyle = new Style({
  image: new CircleStyle({ radius: 6, fill: new Fill({ color: '#ffffff' }), stroke: new Stroke({ color: BLUE, width: 2 }) }),
});

const overlayStyles: Record<string, Style> = {
  'snap-vertex': new Style({ image: new CircleStyle({ radius: 8, stroke: new Stroke({ color: SNAP, width: 2.5 }), fill: new Fill({ color: 'rgba(217,70,239,0.25)' }) }) }),
  'snap-edge': new Style({ image: new RegularShape({ points: 4, radius: 8, stroke: new Stroke({ color: SNAP, width: 2.5 }), fill: new Fill({ color: 'rgba(217,70,239,0.15)' }) }) }),
  overlap: new Style({ fill: new Fill({ color: 'rgba(220,38,38,0.45)' }), stroke: new Stroke({ color: RED, width: 2 }) }),
  clipped: new Style({ fill: new Fill({ color: 'rgba(22,163,74,0.18)' }), stroke: new Stroke({ color: GREEN, width: 2, lineDash: [6, 4] }) }),
  pick: new Style({ stroke: new Stroke({ color: BLUE, width: 6 }) }),
  end: new Style({ image: new CircleStyle({ radius: 7, fill: new Fill({ color: GREEN }), stroke: new Stroke({ color: '#ffffff', width: 2 }) }) }),
};

function overlayStyle(f: FeatureLike): Style | undefined {
  return overlayStyles[f.get('role')];
}
